import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function GolfCharactersConfig({
  gameConfig,
  setGameConfig,
  loading,
}) {
  const { isViewer } = getUserPermissions();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get current characters based on active tab with fallback to default character
  const getCurrentCharacters = () => {
    const isMale = activeTab === 0;
    const charactersKey = isMale ? "charactersMale" : "charactersFemale";

    if (gameConfig[charactersKey] && gameConfig[charactersKey].length > 0) {
      return gameConfig[charactersKey];
    } else {
      return [
        {
          name: isMale ? "Male Character 1" : "Female Character 1",
          power: 50,
          accuracy: 50,
          spin: 50,
        },
      ];
    }
  };

  const handleCharacterChange = (index, field, value) => {
    const isMale = activeTab === 0;
    const charactersKey = isMale ? "charactersMale" : "charactersFemale";
    const currentCharacters = [...getCurrentCharacters()];
    currentCharacters[index] = {
      ...currentCharacters[index],
      [field]: field === 'name' ? value : parseFloat(value) || 0,
    };

    setGameConfig({ ...gameConfig, [charactersKey]: currentCharacters });
  };

  const handleAddCharacter = () => {
    const isMale = activeTab === 0;
    const charactersKey = isMale ? "charactersMale" : "charactersFemale";
    const currentCharacters = getCurrentCharacters();
    const genderPrefix = isMale ? "Male" : "Female";
    const newCharacters = [
      ...currentCharacters,
      {
        name: `${genderPrefix} Character ${currentCharacters.length + 1}`,
        power: 50,
        accuracy: 50,
        spin: 50,
      },
    ];

    setGameConfig({ ...gameConfig, [charactersKey]: newCharacters });
  };

  const handleDeleteCharacter = (index) => {
    const isMale = activeTab === 0;
    const charactersKey = isMale ? "charactersMale" : "charactersFemale";
    const currentCharacters = getCurrentCharacters();
    if (currentCharacters.length <= 1) return;

    const newCharacters = currentCharacters.filter((_, i) => i !== index);

    setGameConfig({ ...gameConfig, [charactersKey]: newCharacters });
  };

  return (
    <Box sx={{ mt: 2, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Golf Characters Configuration
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Configure golf characters with their attributes (power, accuracy, spin)
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#1976d2',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#1565c0',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
            },
          }}
        >
          <Tab label="Male Characters" disabled={loading} />
          <Tab label="Female Characters" disabled={loading} />
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        {getCurrentCharacters().map((character, index) => (
          <Grid item xs={12} key={`character-${index}`}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                "&:hover": {
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {activeTab === 0 ? "Male" : "Female"} Character {index + 1}
                </Typography>
                {!isViewer && (
                  <IconButton
                    onClick={() => handleDeleteCharacter(index)}
                    disabled={loading || getCurrentCharacters().length <= 1}
                    size="small"
                    sx={{
                      color:
                        getCurrentCharacters().length <= 1 ? "#ccc" : "#d32f2f",
                      "&:hover": {
                        backgroundColor:
                          getCurrentCharacters().length <= 1
                            ? "transparent"
                            : "#ffcdd2",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={character.name}
                    onChange={(e) =>
                      handleCharacterChange(index, "name", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#1976d2",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Power"
                    type="number"
                    value={character.power}
                    onChange={(e) =>
                      handleCharacterChange(index, "power", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#e91e63",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Accuracy"
                    type="number"
                    value={character.accuracy}
                    onChange={(e) =>
                      handleCharacterChange(index, "accuracy", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#4caf50",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Spin"
                    type="number"
                    value={character.spin}
                    onChange={(e) =>
                      handleCharacterChange(index, "spin", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#ff9800",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCharacter}
          disabled={loading}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Add New {activeTab === 0 ? "Male" : "Female"} Character
        </Button>
      </Box>
    </Box>
  );
}
